import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Theme, css } from '@emotion/react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { debounce } from 'lodash';
import { colors, mixins, spaces } from '../../core/styles/theme/default';
import { Button, Input, SelectCustom } from '../../primitives';
import { useOutsideClick } from '../../core/utils/Hooks';

const StyledButton = styled(Button)(
  ({ buttonText, theme }: { buttonText?: string; theme?: Theme }) => css`
    border-radius: 0;
    border: none;

    svg {
      path {
        stroke: ${colors.white};
      }
    }

    ${mixins.MQ('medium')} {
      height: ${rem(72)};
    }

    ${mixins.MQMax('medium')} {
      min-width: 0;
      padding: 0 ${rem(17)};

      span {
        ${mixins.visuallyHidden};
      }

      svg {
        margin: 0;
      }
    }

    ${!buttonText &&
    css`
      svg {
        margin: 0;
      }
    `}

    ${theme?.stylesExtension?.searchToolbar?.button}
  `,
);

const StyledSelectCustom = styled(SelectCustom)`
  width: auto;

  .react-select__value-container {
    max-width: 100%;
  }
`;

const StyledInput = styled(Input)`
  flex-grow: 1;

  input {
    ${mixins.MQ('medium')} {
      height: ${rem(72)};
    }
  }

  ${({ theme }) => theme?.stylesExtension?.searchToolbar?.input}
`;

const StyledSuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: ${rem(336)};
  overflow-y: auto;
  margin: ${rem(-1)} 0 0;
  padding: 0;
  list-style: none;
  background: ${colors.white};
  border-radius: 0 0 ${rem(2)} ${rem(2)};
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.06), 0px 4px 6px rgba(0, 0, 0, 0.1);
  z-index: -1;
`;

const StyledSuggestion = styled.button`
  display: block;
  width: 100%;
  padding: 0 ${rem(spaces.gamma)};
  height: ${rem(56)};
  text-overflow: ellipsis;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  border: none;
  border-radius: 0;
  background-color: transparent;
  text-align: left;

  em {
    font-style: normal;
    font-weight: 700;
  }

  &:hover,
  &:focus,
  &[aria-selected='true'] {
    background-color: ${colors.grue100};
  }
`;

const StyledForm = styled.form(
  ({
    searchButtonWidth,
    variant,
    hasSuggestions,
  }: {
    searchButtonWidth: number;
    variant?: string;
    hasSuggestions: boolean;
  }) => css`
    display: flex;
    position: relative;
    z-index: 1;

    ${StyledInput} input {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    ${StyledButton} {
      border-top-right-radius: ${rem(2)};
      border-bottom-right-radius: ${rem(2)};
    }

    ${hasSuggestions &&
    css`
      ${StyledInput} input {
        border-bottom-left-radius: 0;

        &:focus {
          box-shadow: none;
        }
      }

      ${StyledButton} {
        border-bottom-right-radius: 0;
      }
    `}

    ${StyledSelectCustom} {
      ${mixins.MQMax('medium')} {
        position: static;

        > div {
          position: static;
        }
      }

      .react-select__menu {
        width: 100%;
        left: 0;
        border-top-left-radius: 0;
        border-top-right-radius: 0;

        ${mixins.MQ('medium')} {
          width: calc(100% + ${searchButtonWidth}px);
        }
      }

      .react-select__control {
        border-radius: 0;

        ${mixins.responsiveStyles({
          height: { medium: rem(72) },
        })}
      }

      .react-select__single-value {
        padding-right: 0;
        max-width: calc(100% - 80px);
        ${mixins.MQ('medium')} {
          position: static;
          overflow: auto;
          text-overflow: unset;
          white-space: normal;
          max-width: none;
          top: auto;
          transform: none;
        }
      }

      .react-select__value-container {
        ${mixins.responsiveStyles({
          'padding-left': { zero: rem(8), medium: rem(72) },
          'padding-right': { zero: rem(8), medium: rem(spaces.zeta) },
          'min-width': { zero: '0', medium: rem(178) },
        })}
      }

      .react-select__indicator {
        display: flex;

        ${mixins.responsiveStyles({
          left: { medium: rem(spaces.zeta) },
          position: { medium: 'absolute' },
          top: { medium: rem(23) },
          display: { medium: 'flex' },
        })}
      }

      .select__counter {
        ${mixins.responsiveStyles({
          top: { medium: rem(spaces.gamma) },
          right: { medium: rem(spaces.gamma) },
        })}
      }

      .react-select__placeholder {
        ${mixins.MQMax('medium')} {
          ${mixins.visuallyHidden};
        }
      }
    }

    ${variant === 'footer' &&
    css`
      ${StyledButton} {
        height: ${rem(56)};
        min-width: 0;
        padding: 0 ${rem(15)};
        background: rgba(255, 255, 255, 0.1);
        border: none;

        &:hover {
          box-shadow: none;

          i {
            opacity: 1;
          }

          svg path {
            opacity: 1;
          }
        }

        svg {
          margin-right: 0;

          path {
            stroke: ${colors.white};
            opacity: 0.6;
          }
        }

        i {
          padding: 0;
          font-size: ${rem(24)};
          color: ${colors.white};
          opacity: 0.6;
        }

        span {
          ${mixins.visuallyHidden};
        }
      }

      ${StyledInput} input {
        height: ${rem(56)};
        background: rgba(255, 255, 255, 0.1);
        border: none;
        color: rgba(255, 255, 255, 0.6);

        &:hover,
        &:active,
        &:focus {
          color: ${colors.white};
        }
      }
    `}

    ${variant === 'small' &&
    css`
      ${StyledButton} {
        height: ${rem(56)};
        min-width: 0;
        padding: 0 ${rem(15)};

        i {
          padding: 0;
          font-size: ${rem(24)};
        }

        span {
          ${mixins.visuallyHidden};
        }

        svg {
          margin-right: 0;
        }
      }

      ${StyledInput} input {
        height: ${rem(56)};
      }

      ${StyledSelectCustom} {
        position: static;
        width: ${rem(56)};

        > div {
          position: static;
          height: ${rem(56)};
        }

        .react-select__menu {
          width: 100%;
          left: 0;
        }

        .react-select__control {
          height: ${rem(56)};
        }

        .react-select__single-value {
          padding-right: 0;
          max-width: calc(100% - ${rem(80)});
        }

        .react-select__value-container {
          padding-left: rem(28);
          padding-right: rem(28);
          min-width: 0;
        }

        .react-select__indicator {
          display: flex;
          left: ${rem(16)};
        }

        .select__counter {
          top: 0;
          right: 0;
        }

        .react-select__placeholder {
          ${mixins.visuallyHidden};
        }
      }
    `}
  `,
);

const SearchToolbar = ({
  options,
  inputLabel,
  inputPlaceholder,
  searchFilterText,
  searchButtonText,
  onSearch,
  action,
  defaultInputValue,
  defaultFilterValue,
  isMulti = true,
  className,
  variant,
  id,
  filtersHidden = false,
  selectAllLabel,
  renderSelectedValue = false,
  fetchSuggestions,
}: SearchToolbarProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  const [searchButtonWidth, setSearchButtonWidth] = useState(0);
  const [inputValue, setInputValue] = useState(defaultInputValue);
  const [shouldSubmitForm, setShouldSubmitForm] = useState(false);
  const [suggestions, setSuggestions] = useState<
    {
      value: string;
      highlight: string;
    }[]
  >([]);
  const hasSuggestions = suggestions.length > 0;

  const search = (event: React.MouseEvent<HTMLButtonElement>) => {
    // convert node list to an array
    const searchFilterArray =
      formRef.current?.searchFilter?.length > 1
        ? Array.prototype.slice.call(formRef.current?.searchFilter)
        : formRef.current?.searchFilter?.value !== ''
        ? [{ value: formRef.current?.searchFilter?.value }]
        : [];
    const checkedSearchFilters: string[] = searchFilterArray.map(
      (option) => option.value,
    );
    const searchValue = parseInt(
      formRef.current?.searchInput.value as string,
      10,
    );

    if (typeof onSearch === 'function') {
      onSearch(event, searchValue, checkedSearchFilters);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSuggestions = useCallback(
    debounce(async (value: string) => {
      if (fetchSuggestions) {
        setSuggestions(await fetchSuggestions(value.toLowerCase()));
      }
    }, 250),
    [],
  );

  const handleOnChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setInputValue(value);

    if (fetchSuggestions) {
      await debouncedSetSuggestions(value);
    }
  };

  useOutsideClick(formRef, () => {
    if (!suggestions.length) return;

    setSuggestions([]);
  });

  useEffect(() => {
    setTimeout(() => {
      if (searchButtonRef?.current?.getBoundingClientRect().width) {
        setSearchButtonWidth(
          searchButtonRef?.current?.getBoundingClientRect().width,
        );
      }
    }, 250);
  }, []);

  useEffect(() => {
    if (shouldSubmitForm) {
      setShouldSubmitForm(false);
      setSuggestions([]);
      setTimeout(() => {
        formRef.current?.submit();
      }, 0);
    }
  }, [shouldSubmitForm]);

  useEffect(() => {
    const handleEscapeKey = () => {
      setSuggestions([]);
    };

    const handleArrowDownKey = () => {
      const buttons = Array.from(
        suggestionsRef.current?.querySelectorAll('button') ?? [],
      );
      const selectedButton = suggestionsRef.current?.querySelector(
        'button[aria-selected="true"]',
      ) as HTMLButtonElement | null;

      if (selectedButton) {
        const nextIndex =
          (buttons.indexOf(selectedButton) + 1) % buttons.length;
        const nextButton = buttons[nextIndex];

        selectedButton.removeAttribute('aria-selected');
        nextButton.setAttribute('aria-selected', 'true');
      } else if (buttons.length > 0) {
        buttons[0].setAttribute('aria-selected', 'true');
      }
    };

    const handleArrowUpKey = () => {
      const buttons = Array.from(
        suggestionsRef.current?.querySelectorAll('button') ?? [],
      );
      const selectedButton = suggestionsRef.current?.querySelector(
        'button[aria-selected="true"]',
      ) as HTMLButtonElement | null;

      if (selectedButton) {
        const prevIndex =
          (buttons.indexOf(selectedButton) - 1 + buttons.length) %
          buttons.length;
        const prevButton = buttons[prevIndex];

        selectedButton.removeAttribute('aria-selected');
        prevButton.setAttribute('aria-selected', 'true');
      } else if (buttons.length > 0) {
        buttons[buttons.length - 1].setAttribute('aria-selected', 'true');
      }
    };

    const handleTabKey = () => {
      const buttons = Array.from(
        suggestionsRef.current?.querySelectorAll('button') ?? [],
      );
      const selectedButton = suggestionsRef.current?.querySelector(
        'button[aria-selected="true"]',
      ) as HTMLButtonElement | null;

      if (selectedButton) {
        const nextIndex = buttons.indexOf(selectedButton) + 1;

        if (nextIndex < buttons.length) {
          const nextButton = buttons[nextIndex];

          selectedButton.removeAttribute('aria-selected');
          nextButton.setAttribute('aria-selected', 'true');
        } else {
          setSuggestions([]);
        }
      } else if (buttons.length > 0) {
        buttons[0].setAttribute('aria-selected', 'true');
      }
    };

    const handleTabShiftKey = () => {
      const buttons = Array.from(
        suggestionsRef.current?.querySelectorAll('button') ?? [],
      );
      const selectedButton = suggestionsRef.current?.querySelector(
        'button[aria-selected="true"]',
      ) as HTMLButtonElement | null;

      if (selectedButton) {
        const prevIndex = buttons.indexOf(selectedButton) - 1;

        if (prevIndex >= 0) {
          const prevButton = buttons[prevIndex];

          selectedButton.removeAttribute('aria-selected');
          prevButton.setAttribute('aria-selected', 'true');
        } else {
          setSuggestions([]);
        }
      } else if (buttons.length > 0) {
        buttons[0].setAttribute('aria-selected', 'true');
      }
    };

    const handleEnterKey = () => {
      const selectedButton = suggestionsRef.current?.querySelector(
        'button[aria-selected="true"]',
      ) as HTMLButtonElement | null;

      if (selectedButton) {
        setInputValue(selectedButton.getAttribute('data-value') ?? '');
        setShouldSubmitForm(true);
      }
    };

    const getKeyName = (e: KeyboardEvent) => {
      if (['Escape', 'NumpadEscape'].includes(e.key)) return 'Escape';
      if (['ArrowDown', 'NumpadArrowDown'].includes(e.key)) return 'ArrowDown';
      if (['ArrowUp', 'NumpadArrowUp'].includes(e.key)) return 'ArrowUp';
      if (['Tab', 'NumpadTab'].includes(e.key))
        return e.shiftKey ? 'TabShift' : 'Tab';
      if (['Enter', 'NumpadEnter'].includes(e.key)) return 'Enter';
      return null;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!suggestions.length) return;

      const keyName = getKeyName(e);

      if (keyName) {
        e.preventDefault();
      }

      switch (keyName) {
        case 'Escape':
          handleEscapeKey();
          break;
        case 'ArrowDown':
          handleArrowDownKey();
          break;
        case 'ArrowUp':
          handleArrowUpKey();
          break;
        case 'Tab':
          handleTabKey();
          break;
        case 'TabShift':
          handleTabShiftKey();
          break;
        case 'Enter':
          handleEnterKey();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [suggestions]);

  return (
    <StyledForm
      action={action}
      ref={formRef}
      searchButtonWidth={searchButtonWidth}
      className={className}
      variant={variant}
      hasSuggestions={hasSuggestions}
      autoComplete="off"
    >
      <StyledInput
        hideLabel
        label={inputLabel ?? ''}
        placeholder={inputPlaceholder}
        name="searchInput"
        value={inputValue}
        id={id}
        onChangeRequest={(e) => void handleOnChange(e)}
        onFocusRequest={(e) => void handleOnChange(e)}
      />
      {!filtersHidden && options?.length ? (
        <StyledSelectCustom
          options={options}
          isMulti={isMulti}
          name="searchFilter"
          placeholder={searchFilterText}
          selectedValue={defaultFilterValue}
          selectAllLabel={selectAllLabel ?? ''}
          renderSelectedValue={renderSelectedValue}
        />
      ) : null}
      <StyledButton
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => search(e)}
        buttonText={searchButtonText ?? ''}
        iconBefore="EchIconMagnify"
        ref={searchButtonRef}
        type="submit"
      />
      {hasSuggestions ? (
        <StyledSuggestionsList ref={suggestionsRef}>
          {suggestions.map(({ value, highlight }, index) => (
            <li key={`${value}_${index}`}>
              <StyledSuggestion
                type="button"
                dangerouslySetInnerHTML={{ __html: highlight }}
                data-value={value}
                onClick={() => {
                  setInputValue(value);
                  setShouldSubmitForm(true);
                }}
              />
            </li>
          ))}
        </StyledSuggestionsList>
      ) : null}
    </StyledForm>
  );
};

export type SearchToolbarProps = {
  options?: {
    label: string;
    value: string;
  }[];
  inputLabel?: string;
  inputPlaceholder?: string;
  searchFilterText?: string;
  searchButtonText?: string;
  onSearch?: (
    event: React.MouseEvent<HTMLButtonElement>,
    postcode: number,
    checkedTopics: string[],
  ) => void;
  action?: string;
  defaultInputValue?: string;
  defaultFilterValue?: string[];
  isMulti?: boolean;
  className?: string;
  variant?: string;
  id?: string;
  filtersHidden?: boolean;
  selectAllLabel?: string;
  renderSelectedValue?: boolean;
  fetchSuggestions?: (query: string) => Promise<
    {
      value: string;
      highlight: string;
    }[]
  >;
};

export default SearchToolbar;
